'use client';

import { useEffect, useState } from 'react';
import { Eye, GripVertical } from 'lucide-react';
import { useToast } from '@/components/admin/Toast';
import type { TenantRow } from '../types';

export function LandingPanel({
  tenants,
  loading,
  onRefresh,
}: {
  tenants: TenantRow[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const { toast } = useToast();
  const [items, setItems] = useState<TenantRow[]>([]);
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => {
    setItems([...tenants].sort((a, b) => a.landing_order - b.landing_order));
  }, [tenants]);

  const onDrop = async (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const from = items.findIndex((t) => t.id === dragId);
    const to = items.findIndex((t) => t.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    setDragId(null);
    const orders = next.map((t, i) => ({ id: t.id, landing_order: i + 1 }));
    await fetch('/api/super-admin/tenants/reorder', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orders }),
    });
    toast('Ordem da landing atualizada');
    onRefresh();
  };

  const toggleVisible = async (t: TenantRow) => {
    await fetch(`/api/super-admin/tenants/${t.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ landing_visible: !t.landing_visible }),
    });
    onRefresh();
  };

  if (loading) return <p className="text-sm text-ink-500">A carregar...</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-500">
        Arrasta para reordenar a landing page em okai.pt. Usa o toggle para mostrar/ocultar.
      </p>
      <div className="space-y-2">
        {items.map((t) => (
          <div
            key={t.id}
            draggable
            onDragStart={() => setDragId(t.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(t.id)}
            className="sa-list-row cursor-grab active:cursor-grabbing"
          >
            <div className="flex items-center gap-3 min-w-0">
              <GripVertical className="w-4 h-4 text-ink-500 flex-shrink-0" />
              <span className="text-xs font-mono text-ink-500 w-6">{t.landing_order}</span>
              <div className="min-w-0">
                <p className="font-medium truncate">{t.name}</p>
                <p className="text-xs text-ink-500 font-mono">{t.slug}</p>
              </div>
            </div>
            <button type="button" onClick={() => toggleVisible(t)} className={`sa-chip ${t.landing_visible ? 'sa-chip-brand' : ''}`}>
              <Eye className="w-3.5 h-3.5" />
              {t.landing_visible ? 'Visivel' : 'Oculto'}
            </button>
          </div>
        ))}
      </div>
      <a href="https://okai.pt" target="_blank" rel="noreferrer" className="sa-chip inline-flex">
        Preview landing →
      </a>
    </div>
  );
}
